// Pure helpers for the structured lifecare plan: totals/subtotals, empty value builders,
// formik field paths and the autosave field path mapping. Keep math in one place so the
// UI never recomputes totals inline (the schema treats them as derived, not editable).

export const TOTAL_SUMMARY_KEY = 'TOTAL';

// stable id for a subsection so it stays matchable across AI edits (rename without duplicating)
const generateSubsectionId = () =>
  (globalThis.crypto?.randomUUID?.() || `sub-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const toFiniteNumber = (value) => {
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
  if (value === null || value === undefined || value === '') return 0;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const LIFE_EXPECTANCY_LABEL = 'Life Expectancy';

const EMPTY_AGE_YEAR_TEXT = '--';

// the care span replaces the old totalSessions cell: sessions are implied by
// frequencyPerYear over the number of years between the beginning and ending age
export const computeRowDurationYears = (row) => {
  const span = toFiniteNumber(row?.endingAge) - toFiniteNumber(row?.beginningAge);
  return span > 0 ? span : 0;
};

export const computeRowLifetimeCost = (row) => {
  return (
    toFiniteNumber(row?.frequencyPerYear) *
    computeRowDurationYears(row) *
    toFiniteNumber(row?.unitCost)
  );
};

const formatAgeYear = ({ age, year }) => {
  const ageValue = toFiniteNumber(age);
  const yearValue = toFiniteNumber(year);
  if (ageValue <= 0 && yearValue <= 0) return EMPTY_AGE_YEAR_TEXT;
  return `${ageValue > 0 ? ageValue : EMPTY_AGE_YEAR_TEXT} / ${yearValue > 0 ? yearValue : EMPTY_AGE_YEAR_TEXT}`;
};

// the two sub rows rendered inside the Age / Year cell of every read-only table
export const buildCareSpanLines = (row) => [
  {
    label: 'Beginning',
    text: formatAgeYear({ age: row?.beginningAge, year: row?.beginningYear }),
  },
  {
    label: 'Ending',
    text: row?.isEndingLifeExpectancy
      ? LIFE_EXPECTANCY_LABEL
      : formatAgeYear({ age: row?.endingAge, year: row?.endingYear }),
  },
];

// The stored lifetimeCost is what every other stage sums (the PDF's sumLifetimeCost helper,
// the server's enrich step, the persistence schema), so subtotals read it instead of
// recomputing. Rows saved before the age span replaced totalSessions have no span at all, and
// recomputing would collapse their real cost to zero across the subtotal and summary tabs.
export const getRowLifetimeCost = (row) => toFiniteNumber(row?.lifetimeCost);

// only these cells feed the frequency * span * unitCost formula, so editing any other cell
// leaves a row's stored cost alone
const ROW_COST_INPUT_FIELDS = new Set([
  'beginningAge',
  'endingAge',
  'frequencyPerYear',
  'unitCost',
]);

export const isRowCostInputField = (field) => ROW_COST_INPUT_FIELDS.has(field);

export const computeSubsectionSubtotal = (subsection) => {
  const tables = Array.isArray(subsection?.tables) ? subsection.tables : [];
  return tables.reduce((acc, row) => acc + getRowLifetimeCost(row), 0);
};

export const computeTotalLifetimeCost = (plan) => {
  const subsections = plan?.futureCareNeeds?.subsections;
  if (!Array.isArray(subsections)) return 0;
  return subsections.reduce((acc, subsection) => acc + computeSubsectionSubtotal(subsection), 0);
};

export const buildSummaryLines = (plan) => {
  const subsections = plan?.futureCareNeeds?.subsections;
  if (!Array.isArray(subsections)) return [{ subsectionTitle: TOTAL_SUMMARY_KEY, subtotal: 0 }];
  const subsectionLines = subsections.map((subsection) => ({
    subsectionTitle: subsection?.title || '',
    subtotal: computeSubsectionSubtotal(subsection),
  }));
  const total = subsectionLines.reduce((acc, line) => acc + line.subtotal, 0);
  return [...subsectionLines, { subsectionTitle: TOTAL_SUMMARY_KEY, subtotal: total }];
};

// coerce the numeric row cells and rebuild summary / totalLifetimeCost from the stored row
// costs so the data we persist always satisfies the structured schema constraints. A row's own
// lifetimeCost is re-derived when the user edits one of its cost inputs, never here, so
// autosaving an untouched legacy row cannot zero out the cost the generator produced.
export const enrichPlanWithDerivedTotals = (plan) => {
  if (!plan || typeof plan !== 'object') return plan;
  const subsections = Array.isArray(plan?.futureCareNeeds?.subsections)
    ? plan.futureCareNeeds.subsections.map((subsection) => ({
        ...subsection,
        tables: (Array.isArray(subsection?.tables) ? subsection.tables : []).map((row) => ({
          ...row,
          beginningAge: toFiniteNumber(row?.beginningAge),
          beginningYear: toFiniteNumber(row?.beginningYear),
          endingAge: toFiniteNumber(row?.endingAge),
          endingYear: toFiniteNumber(row?.endingYear),
          isEndingLifeExpectancy: Boolean(row?.isEndingLifeExpectancy),
          frequencyPerYear: toFiniteNumber(row?.frequencyPerYear),
          unitCost: toFiniteNumber(row?.unitCost),
          lifetimeCost: getRowLifetimeCost(row),
        })),
      }))
    : [];
  const enrichedPlan = {
    ...plan,
    futureCareNeeds: { ...(plan.futureCareNeeds || {}), subsections },
  };
  enrichedPlan.summary = buildSummaryLines(enrichedPlan);
  enrichedPlan.totalLifetimeCost = computeTotalLifetimeCost(enrichedPlan);
  return enrichedPlan;
};

// Rows saved before the age/year span replaced totalSessions carry no span, so their duration
// reads as 0 and every derived cost collapses to zero even though a real lifetimeCost is stored.
// The span length is recoverable from the legacy row itself: totalSessions spread over
// frequencyPerYear, or the stored cost divided by the yearly spend. Typed mongoose reads drop
// totalSessions (the path no longer exists on the schema), hence the cost-based fallback.
const computeLegacySpanYears = (row) => {
  const frequencyPerYear = toFiniteNumber(row?.frequencyPerYear);
  if (frequencyPerYear <= 0) return 0;
  const totalSessions = toFiniteNumber(row?.totalSessions);
  if (totalSessions > 0) return totalSessions / frequencyPerYear;
  const yearlySpend = frequencyPerYear * toFiniteNumber(row?.unitCost);
  if (yearlySpend <= 0) return 0;
  return toFiniteNumber(row?.lifetimeCost) / yearlySpend;
};

// a row the user deliberately collapsed (endingAge === beginningAge) still has a span and is
// left alone; only rows with no age data at all are treated as pre-migration
const hasCareSpan = (row) =>
  toFiniteNumber(row?.beginningAge) > 0 || toFiniteNumber(row?.endingAge) > 0;

// ages have to stay whole numbers to render sensibly, so the reconstructed span is rounded and
// the row's cost is realigned to it, keeping the row, its subtotal and the summary in agreement
export const backfillLegacyCareSpan = ({ row, currentAge }) => {
  if (hasCareSpan(row)) return row;
  const spanYears = Math.round(computeLegacySpanYears(row));
  if (spanYears <= 0) return row;
  const beginningAge = toFiniteNumber(currentAge);
  const beginningYear = toFiniteNumber(row?.beginningYear);
  const nextRow = {
    ...row,
    beginningAge,
    beginningYear,
    endingAge: beginningAge + spanYears,
    // a legacy row has no calendar years to carry over, so leave them blank rather than invent them
    endingYear: beginningYear > 0 ? beginningYear + spanYears : 0,
  };
  return { ...nextRow, lifetimeCost: computeRowLifetimeCost(nextRow) };
};

export const buildEmptyTableRow = () => ({
  item: '',
  beginningAge: 0,
  beginningYear: 0,
  endingAge: 0,
  endingYear: 0,
  isEndingLifeExpectancy: false,
  frequencyPerYear: 0,
  frequencyComment: '',
  purpose: '',
  unitCost: 0,
  lifetimeCost: 0,
});

export const buildEmptySubsection = () => ({
  id: generateSubsectionId(),
  title: 'New subsection',
  content: '',
  tables: [buildEmptyTableRow()],
});

const UNTITLED_SUBSECTION_LABEL = 'Untitled subsection';

export const isValidRowMove = ({ subsections, subsectionIndex, rowIndex, targetSubsectionIndex }) => {
  if (!Array.isArray(subsections)) return false;
  if (subsectionIndex === targetSubsectionIndex) return false;
  if (!subsections[targetSubsectionIndex]) return false;
  return Boolean(subsections[subsectionIndex]?.tables?.[rowIndex]);
};

// appends the row to the bottom of the target subsection and drops it from the source;
// returns null when the move isn't valid so the caller can skip the state update
export const moveRowBetweenSubsections = ({
  subsections,
  subsectionIndex,
  rowIndex,
  targetSubsectionIndex,
}) => {
  if (!isValidRowMove({ subsections, subsectionIndex, rowIndex, targetSubsectionIndex })) return null;
  const movedRow = subsections[subsectionIndex].tables[rowIndex];
  return subsections.map((subsection, index) => {
    if (index === subsectionIndex) {
      return { ...subsection, tables: subsection.tables.filter((_, idx) => idx !== rowIndex) };
    }
    if (index === targetSubsectionIndex) {
      const tables = Array.isArray(subsection?.tables) ? subsection.tables : [];
      return { ...subsection, tables: [...tables, movedRow] };
    }
    return subsection;
  });
};

// the other tables a row can be moved into, labeled for the row move menu
export const buildRowMoveTargets = ({ subsections, currentIndex }) => {
  const safeSubsections = Array.isArray(subsections) ? subsections : [];
  return safeSubsections.reduce((targets, subsection, index) => {
    if (index === currentIndex) return targets;
    const title = typeof subsection?.title === 'string' ? subsection.title.trim() : '';
    targets.push({ index, title: title || `${UNTITLED_SUBSECTION_LABEL} ${index + 1}` });
    return targets;
  }, []);
};

export const buildEmptyPlan = ({ clientName, currentAge, dateOfIncident } = {}) => ({
  info: {
    clientName: clientName || '',
    currentAge: toFiniteNumber(currentAge),
    dateOfIncident: dateOfIncident || '',
  },
  diagnosis: { title: 'Diagnosis', content: '' },
  lifeExpectancy: { title: 'Life Expectancy', content: '', lifeExpectancy: 0 },
  summaryOfRecords: { title: 'Summary of Records Reviewed', content: '' },
  futureCareNeeds: { title: 'Future Care Needs', subsections: [] },
  summary: [{ subsectionTitle: TOTAL_SUMMARY_KEY, subtotal: 0 }],
  totalLifetimeCost: 0,
});

// merge fetched plan with defaults so the form always sees the full shape even if
// some fields were missing in mongo (e.g. plans generated before a schema add).
export const normalizePlanForForm = (plan) => {
  const base = buildEmptyPlan();
  if (!plan || typeof plan !== 'object') return base;
  const currentAge = plan?.info?.currentAge;
  return {
    info: { ...base.info, ...(plan.info || {}) },
    diagnosis: { ...base.diagnosis, ...(plan.diagnosis || {}) },
    lifeExpectancy: { ...base.lifeExpectancy, ...(plan.lifeExpectancy || {}) },
    summaryOfRecords: { ...base.summaryOfRecords, ...(plan.summaryOfRecords || {}) },
    futureCareNeeds: {
      ...base.futureCareNeeds,
      ...(plan.futureCareNeeds || {}),
      subsections: Array.isArray(plan?.futureCareNeeds?.subsections)
        ? plan.futureCareNeeds.subsections.map((subsection) => ({
            // keep the stable id so it survives the form round-trip / autosave
            ...(subsection?.id ? { id: subsection.id } : {}),
            title: subsection?.title || '',
            content: subsection?.content || '',
            tables: Array.isArray(subsection?.tables)
              ? subsection.tables.map((row) =>
                  backfillLegacyCareSpan({ row: { ...buildEmptyTableRow(), ...row }, currentAge })
                )
              : [],
          }))
        : [],
    },
    summary: Array.isArray(plan.summary) ? plan.summary : base.summary,
    totalLifetimeCost: toFiniteNumber(plan.totalLifetimeCost),
  };
};

export const getClientLifecarePlanFromCase = ({ caseDetails, clientId }) => {
  const client = (caseDetails?.clients || []).find((item) => item?.clientId === clientId);
  return client?.medicalProviders?.postAccident?.result?.lifecarePlanStructured || null;
};

export const getClientDisplayName = ({ client, index }) =>
  client?.personalInfo?.clientName || `Plaintiff ${index + 1}`;

// path that the lambda updateCase handler resolves via the {clientId:xxx} array-lookup syntax
export const getLifecarePlanStructuredFieldPath = (clientId) =>
  `clients.{clientId:${clientId}}.medicalProviders.postAccident.result.lifecarePlanStructured`;

// the legacy markdown string is what the existing PDF pipeline still renders. Clearing it
// here ensures the structured plan's regenerated markdown wins on the next preview.
export const getLifecarePlanLegacyFieldPath = (clientId) =>
  `clients.{clientId:${clientId}}.medicalProviders.postAccident.result.lifecarePlan`;

export const getLifecarePlanTotalCostFieldPath = (clientId) =>
  `clients.{clientId:${clientId}}.medicalProviders.postAccident.result.lifecarePlanTotalCost`;

export const formatCurrency = (value) => {
  const num = toFiniteNumber(value);
  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const toNumberInputValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value);
};

// keeps the raw string for typing in progress (e.g. "" while clearing, "1.") but
// clamps any parseable negative number to "0" so cost/frequency/sessions never go below zero
export const clampNonNegativeNumberInputValue = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return value;
  return parsed < 0 ? '0' : value;
};

// `<input type="number">` natively accepts "-", "+", "e", "E" as typed characters.
// We block them at the keystroke so the field can never even *display* a negative
// or scientific-notation value. Paste / programmatic input is still caught by
// clampNonNegativeNumberInputValue on change.
const BLOCKED_NUMBER_INPUT_KEYS = new Set(['-', '+', 'e', 'E']);

export const blockNegativeNumberInputKeys = (event) => {
  if (BLOCKED_NUMBER_INPUT_KEYS.has(event.key)) {
    event.preventDefault();
  }
};

const isBlankString = (value) => typeof value !== 'string' || value.trim() === '';

const isMeaningfulRow = (row) => {
  if (!row || typeof row !== 'object') return false;
  if (!isBlankString(row.item)) return true;
  if (!isBlankString(row.purpose)) return true;
  if (!isBlankString(row.frequencyComment)) return true;
  if (toFiniteNumber(row.frequencyPerYear) > 0) return true;
  if (computeRowDurationYears(row) > 0) return true;
  if (toFiniteNumber(row.unitCost) > 0) return true;
  return false;
};

const sanitizeTextBlock = (block) => {
  if (!block || isBlankString(block.content)) return null;
  return block;
};

const sanitizeLifeExpectancyBlock = (block) => {
  if (!block) return null;
  const hasContent = !isBlankString(block.content);
  const hasYears = toFiniteNumber(block.lifeExpectancy) > 0;
  return hasContent || hasYears ? block : null;
};

const sanitizeSubsection = (subsection) => {
  if (!subsection || typeof subsection !== 'object') return null;
  const tables = Array.isArray(subsection.tables)
    ? subsection.tables.filter(isMeaningfulRow)
    : [];
  const hasTitle = !isBlankString(subsection.title);
  const hasContent = !isBlankString(subsection.content);
  if (!hasTitle && !hasContent && tables.length === 0) return null;
  return { ...subsection, tables };
};

const sanitizeFutureCareNeeds = (block) => {
  if (!block) return null;
  const subsections = Array.isArray(block.subsections)
    ? block.subsections.map(sanitizeSubsection).filter(Boolean)
    : [];
  if (subsections.length === 0 && isBlankString(block.title)) return null;
  return { ...block, subsections };
};

// drops sections whose user-editable content is blank so the rendered PDF doesn't
// show empty Diagnosis / Life Expectancy / Summary / subsection headings. info is
// always kept because it identifies the patient; derived totals (summary,
// totalLifetimeCost) are recomputed downstream by enrichPlanWithDerivedTotals.
export const sanitizeLifecarePlanForPreview = (plan) => {
  if (!plan || typeof plan !== 'object') return plan;
  const sanitized = { info: plan.info };
  const diagnosis = sanitizeTextBlock(plan.diagnosis);
  if (diagnosis) sanitized.diagnosis = diagnosis;
  const lifeExpectancy = sanitizeLifeExpectancyBlock(plan.lifeExpectancy);
  if (lifeExpectancy) sanitized.lifeExpectancy = lifeExpectancy;
  const summaryOfRecords = sanitizeTextBlock(plan.summaryOfRecords);
  if (summaryOfRecords) sanitized.summaryOfRecords = summaryOfRecords;
  const futureCareNeeds = sanitizeFutureCareNeeds(plan.futureCareNeeds);
  if (futureCareNeeds) sanitized.futureCareNeeds = futureCareNeeds;
  return sanitized;
};

// formik field path builders kept centralized so the form components never hand-construct paths
export const planFieldPaths = {
  info: (field) => `info.${field}`,
  diagnosis: (field) => `diagnosis.${field}`,
  lifeExpectancy: (field) => `lifeExpectancy.${field}`,
  summaryOfRecords: (field) => `summaryOfRecords.${field}`,
  futureCareNeedsTitle: 'futureCareNeeds.title',
  subsections: 'futureCareNeeds.subsections',
  subsection: (index) => `futureCareNeeds.subsections.${index}`,
  subsectionField: (index, field) => `futureCareNeeds.subsections.${index}.${field}`,
  subsectionTables: (index) => `futureCareNeeds.subsections.${index}.tables`,
  tableRow: (subsectionIndex, rowIndex) =>
    `futureCareNeeds.subsections.${subsectionIndex}.tables.${rowIndex}`,
  tableCell: (subsectionIndex, rowIndex, field) =>
    `futureCareNeeds.subsections.${subsectionIndex}.tables.${rowIndex}.${field}`,
};

export const isTotalSummaryLine = (line) => line?.subsectionTitle === TOTAL_SUMMARY_KEY;
